import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";

interface MemberRow {
  id: string;
  email: string | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
}

interface GuideRow {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  content: string;
  category: string | null;
  publish_month: string | null;
  is_published: boolean;
  emailed_at: string | null;
}

interface QuoteCheckRow {
  id: string;
  trade: string;
  job_description: string;
  quoted_amount: number;
  contractor_name: string | null;
  location: string | null;
  status: string;
  verdict: string | null;
  fair_price_low: number | null;
  fair_price_high: number | null;
  admin_response: string | null;
  created_at: string;
}

const emptyGuide = {
  title: "",
  slug: "",
  summary: "",
  content: "",
  category: "",
  publish_month: new Date().toISOString().slice(0, 8) + "01",
};

export function AdminMembershipManagement() {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [guides, setGuides] = useState<GuideRow[]>([]);
  const [checks, setChecks] = useState<QuoteCheckRow[]>([]);
  const [guideForm, setGuideForm] = useState(emptyGuide);
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState<Record<string, { verdict: string; low: string; high: string; response: string }>>({});
  const { toast } = useToast();

  const loadAll = async () => {
    const [m, g, q] = await Promise.all([
      supabase.from("memberships").select("id, email, status, current_period_end, cancel_at_period_end, created_at").order("created_at", { ascending: false }),
      supabase.from("membership_guides").select("*").order("publish_month", { ascending: false }),
      supabase.from("quote_checks").select("*").order("created_at", { ascending: false }),
    ]);
    if (m.error) console.error("Members load:", m.error.message);
    if (g.error) console.error("Guides load:", g.error.message);
    if (q.error) console.error("Quote checks load:", q.error.message);
    setMembers((m.data as MemberRow[]) ?? []);
    setGuides((g.data as GuideRow[]) ?? []);
    setChecks((q.data as QuoteCheckRow[]) ?? []);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveGuide = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!guideForm.title.trim() || !guideForm.content.trim()) {
      toast({ title: "Title and content are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const slug =
        guideForm.slug.trim() ||
        guideForm.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");

      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from("membership_guides").insert({
        title: guideForm.title.trim(),
        slug,
        summary: guideForm.summary.trim() || null,
        content: guideForm.content.trim(),
        category: guideForm.category.trim() || null,
        publish_month: guideForm.publish_month || null,
        is_published: true,
        created_by: auth.user?.id ?? null,
      });
      if (error) throw error;
      toast({ title: "Guide published" });
      setGuideForm(emptyGuide);
      loadAll();
    } catch (error: any) {
      console.error("Guide save failed:", error);
      toast({ title: "Could not save guide", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const emailGuide = async (guideId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("send-membership-guide", {
        body: { guide_id: guideId },
      });
      if (error) throw error;
      toast({
        title: "Guide emailed",
        description: `Sent to ${data?.sent ?? 0} members.`,
      });
      loadAll();
    } catch (error: any) {
      console.error("Guide email failed:", error);
      toast({ title: "Could not email guide", variant: "destructive" });
    }
  };

  const answerCheck = async (id: string) => {
    const entry = responses[id];
    if (!entry?.response?.trim()) {
      toast({ title: "Write a response first", variant: "destructive" });
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("quote_checks")
      .update({
        status: "answered",
        verdict: entry.verdict || null,
        fair_price_low: entry.low ? Number(entry.low) : null,
        fair_price_high: entry.high ? Number(entry.high) : null,
        admin_response: entry.response.trim(),
        reviewed_by: auth.user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", id);
    if (error) {
      console.error("Quote check update failed:", error.message);
      toast({ title: "Could not save response", variant: "destructive" });
      return;
    }
    toast({ title: "Response sent" });
    loadAll();
  };

  const activeCount = members.filter((m) => ["active", "trialing"].includes(m.status)).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active members</CardDescription>
            <CardTitle className="text-3xl">{activeCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly revenue</CardDescription>
            <CardTitle className="text-3xl">£{(activeCount * 19.99).toFixed(2)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending quote checks</CardDescription>
            <CardTitle className="text-3xl">
              {checks.filter((c) => c.status !== "answered").length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="members">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="guides">Guides</TabsTrigger>
          <TabsTrigger value="quotes">Quote checks</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4 space-y-3">
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No memberships yet.</p>
          ) : (
            members.map((member) => (
              <Card key={member.id} className="border-border/50">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base">{member.email ?? "Unknown email"}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={["active", "trialing"].includes(member.status) ? "default" : "secondary"}>
                        {member.status}
                      </Badge>
                      {member.cancel_at_period_end && <Badge variant="outline">Cancelling</Badge>}
                    </div>
                  </div>
                  <CardDescription>
                    {member.current_period_end
                      ? `Period ends ${new Date(member.current_period_end).toLocaleDateString("en-GB")}`
                      : "No renewal date"}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="guides" className="mt-4 space-y-6">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Publish a monthly guide</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveGuide} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="g-title">Title</Label>
                    <Input
                      id="g-title"
                      value={guideForm.title}
                      onChange={(e) => setGuideForm({ ...guideForm, title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="g-category">Category</Label>
                    <Input
                      id="g-category"
                      value={guideForm.category}
                      onChange={(e) => setGuideForm({ ...guideForm, category: e.target.value })}
                      placeholder="Plumbing, Winter prep…"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="g-month">Publish month</Label>
                    <Input
                      id="g-month"
                      type="date"
                      value={guideForm.publish_month}
                      onChange={(e) => setGuideForm({ ...guideForm, publish_month: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="g-slug">Slug (optional)</Label>
                    <Input
                      id="g-slug"
                      value={guideForm.slug}
                      onChange={(e) => setGuideForm({ ...guideForm, slug: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="g-summary">Summary</Label>
                  <Input
                    id="g-summary"
                    value={guideForm.summary}
                    onChange={(e) => setGuideForm({ ...guideForm, summary: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="g-content">Content</Label>
                  <Textarea
                    id="g-content"
                    rows={8}
                    value={guideForm.content}
                    onChange={(e) => setGuideForm({ ...guideForm, content: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={saving}>
                  {saving ? "Publishing…" : "Publish guide"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {guides.map((guide) => (
            <Card key={guide.id} className="border-border/50">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-base">{guide.title}</CardTitle>
                  <div className="flex items-center gap-2">
                    {guide.emailed_at ? (
                      <Badge variant="secondary">
                        Emailed {new Date(guide.emailed_at).toLocaleDateString("en-GB")}
                      </Badge>
                    ) : (
                      <Button size="sm" onClick={() => emailGuide(guide.id)}>
                        Email to members
                      </Button>
                    )}
                  </div>
                </div>
                {guide.summary && <CardDescription>{guide.summary}</CardDescription>}
              </CardHeader>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="quotes" className="mt-4 space-y-4">
          {checks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No quote checks submitted yet.</p>
          ) : (
            checks.map((check) => {
              const entry = responses[check.id] ?? { verdict: "", low: "", high: "", response: "" };
              return (
                <Card key={check.id} className="border-border/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle className="text-base">
                        {check.trade} — £{Number(check.quoted_amount).toFixed(2)}
                      </CardTitle>
                      <Badge variant={check.status === "answered" ? "secondary" : "default"}>
                        {check.status}
                      </Badge>
                    </div>
                    <CardDescription>
                      {check.job_description}
                      {check.location ? ` · ${check.location}` : ""}
                      {check.contractor_name ? ` · ${check.contractor_name}` : ""}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {check.status === "answered" ? (
                      <div className="text-sm space-y-1">
                        {check.verdict && <p className="font-medium">Verdict: {check.verdict}</p>}
                        <p className="text-muted-foreground">{check.admin_response}</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input
                            placeholder="Verdict (fair / high / low)"
                            value={entry.verdict}
                            onChange={(e) =>
                              setResponses({ ...responses, [check.id]: { ...entry, verdict: e.target.value } })
                            }
                          />
                          <Input
                            type="number"
                            placeholder="Fair low (£)"
                            value={entry.low}
                            onChange={(e) =>
                              setResponses({ ...responses, [check.id]: { ...entry, low: e.target.value } })
                            }
                          />
                          <Input
                            type="number"
                            placeholder="Fair high (£)"
                            value={entry.high}
                            onChange={(e) =>
                              setResponses({ ...responses, [check.id]: { ...entry, high: e.target.value } })
                            }
                          />
                        </div>
                        <Textarea
                          rows={3}
                          placeholder="Your response to the member"
                          value={entry.response}
                          onChange={(e) =>
                            setResponses({ ...responses, [check.id]: { ...entry, response: e.target.value } })
                          }
                        />
                        <Button size="sm" onClick={() => answerCheck(check.id)}>
                          Send response
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
